// src/pages/Platform/Companies.js
//
// The Super Admin's main dashboard — every Company row (ListCompaniesHandler,
// internal/handlers/platform_company_handler.go), with the search/status
// filter/pagination shape this codebase already uses elsewhere (e.g.
// Users.js), plus the "add a company and have it immediately, automatically
// get a working database... and a live HTTPS subdomain" flow this feature's
// own plan doc asks for (see AddCompanyModal below). Deliberately built with
// plain markup rather than reusing components/UI/DataTable.jsx — that
// component depends on I18nContext (useTranslation) and a --primary-color
// CSS variable sourced from a tenant's own Config > Appearance setting,
// neither of which exists anywhere in the Platform tree (see
// PlatformDashboardLayout.js's own doc comment) — reusing it here would mean
// either mounting tenant-only providers inside the Platform app (defeating
// the whole point of keeping the two trees isolated) or silently crashing
// on an undefined context.
import { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCompanies,
  createCompany,
  retryCompanyProvisioning,
  getCompanyById,
} from "../../services/api";
import { PlatformAuthContext } from "../../context/PlatformAuthContext";

const STATUS_STYLES = {
  provisioning: { bg: "#fef3c7", fg: "#92400e", label: "Provisioning" },
  active: { bg: "#dcfce7", fg: "#166534", label: "Active" },
  grace: { bg: "#fef3c7", fg: "#92400e", label: "Grace Period" },
  suspended: { bg: "#fee2e2", fg: "#991b1b", label: "Suspended" },
  cancelled: { bg: "#e2e8f0", fg: "#334155", label: "Cancelled" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { bg: "#e2e8f0", fg: "#334155", label: status };
  return (
    <span
      style={{
        background: s.bg,
        color: s.fg,
        borderRadius: 999,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

function ProvisioningBadge({ company }) {
  if (company.provisioning_status === "ready") return null;
  const isFailed = company.provisioning_status === "tls_failed";
  return (
    <div style={{ fontSize: 11, marginTop: 4, color: isFailed ? "#991b1b" : "#92400e" }}>
      {isFailed ? "TLS provisioning failed" : `Provisioning: ${company.provisioning_status}`}
      {company.provisioning_error ? ` — ${company.provisioning_error}` : ""}
    </div>
  );
}

// provisioningSteps turns the two real checkpoints ProvisionCompany
// (internal/handlers/platform_company_handler.go) actually reports —
// ProvisioningStatus going pending -> db_ready -> (ready | tls_failed) —
// into the two-stage progress list ProvisioningProgress renders below.
// Deliberately only two stages, not three: provisionTLSStep's own success
// branch sets ProvisioningStatus="ready" and Status="active" together, in
// the same step, so "issuing the certificate" and "activating the company"
// are truly one moment on the backend, not two — inventing a third row here
// would just be a fake extra step with nothing real to wait on.
function provisioningSteps(company) {
  const ps = company.provisioning_status;
  const dbDone = ps !== "pending";
  const tlsDone = ps === "ready";
  const tlsFailed = ps === "tls_failed";

  return [
    {
      key: "db",
      label: "Setting up the database & default data",
      state: dbDone ? "done" : "active",
    },
    {
      key: "tls",
      label: "Issuing the HTTPS certificate & activating",
      state: !dbDone ? "waiting" : tlsFailed ? "failed" : tlsDone ? "done" : "active",
    },
  ];
}

function StepIcon({ state }) {
  const glyph = { done: "✓", active: "●", failed: "✗", waiting: "○" }[state];
  const color = { done: "#16a34a", active: "#2563eb", failed: "#dc2626", waiting: "#cbd5e1" }[state];
  return <span style={{ color, fontWeight: 700, width: 16, display: "inline-block" }}>{glyph}</span>;
}

// ProvisioningProgress replaces what used to be a single static "Refresh
// this list to watch its status" line in AddCompanyModal's success screen
// (see this feature's own history — the same "I clicked and felt nothing
// change" gap CompanyDetail.js's Retry button had before it grew its own
// polling) with a live view of the SAME background ProvisionCompany call
// that CreateCompanyHandler already kicks off — this component doesn't
// start any new work, it just watches the existing company row until that
// background call finishes (or the certbot/TLS step genuinely times out —
// see provisionTLSStep's own 2-minute context deadline in
// internal/platform/provision_tls.go, which is why this polls for longer
// than CompanyDetail.js's own retry-polling does).
function ProvisioningProgress({ token, companyId, initialCompany, onViewCompany }) {
  const [company, setCompany] = useState(initialCompany);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40; // ~2 minutes at 3s each, just past provisionTLSStep's own timeout
    let timeoutId;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await getCompanyById(token, companyId);
        if (cancelled) return;
        setCompany(res.company);
        if (res.company.provisioning_status === "ready" || res.company.provisioning_status === "tls_failed") {
          return; // done — stop scheduling further polls
        }
      } catch {
        // A transient network hiccup shouldn't stop the progress view —
        // just try again on the next tick.
      }
      if (attempts >= maxAttempts) {
        if (!cancelled) setTimedOut(true);
        return;
      }
      timeoutId = setTimeout(tick, 3000);
    };

    timeoutId = setTimeout(tick, 3000);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, companyId]);

  const steps = provisioningSteps(company);
  const finished = company.provisioning_status === "ready" || company.provisioning_status === "tls_failed";

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>
        Provisioning progress
      </div>
      <div style={progressBoxStyle}>
        {steps.map((step) => (
          <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
            <StepIcon state={step.state} />
            <span style={{ fontSize: 13, color: step.state === "waiting" ? "#94a3b8" : "#334155" }}>
              {step.label}
            </span>
          </div>
        ))}
        {company.provisioning_status === "tls_failed" && (
          <div style={{ fontSize: 12, color: "#991b1b", marginTop: 6 }}>
            {company.provisioning_error || "The HTTPS step failed."} You can open the company's page to retry,
            or activate it manually if this keeps happening (e.g. in local development).
          </div>
        )}
        {company.provisioning_status === "ready" && (
          <div style={{ fontSize: 12, color: "#166534", marginTop: 6 }}>
            {company.subdomain}.posflect.com is live.
          </div>
        )}
        {!finished && timedOut && (
          <div style={{ fontSize: 12, color: "#92400e", marginTop: 6 }}>
            Still working — this can occasionally take longer than usual. You can close this and check the
            company's own page anytime; it'll pick up right where it left off.
          </div>
        )}
        {!finished && !timedOut && (
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>Checking every few seconds…</div>
        )}
      </div>
      <button
        type="button"
        style={{ ...linkBtnStyle, marginLeft: 0, marginTop: 6 }}
        onClick={() => onViewCompany(companyId)}
      >
        View company page
      </button>
    </div>
  );
}

// AddCompanyModal is this screen's own equivalent of a plan-named
// "AddEditCompany.js" — CREATE only, deliberately: the backend has no
// generic "update a company" endpoint at all (Name/Subdomain are immutable
// once provisioned — see models.Company's own doc comment — and every
// other field changes through its own dedicated action: renew/suspend/
// reactivate/retry-provisioning, each already its own button on this
// screen/CompanyDetail.js, not a form field here).
function AddCompanyModal({ token, onClose, onCreated, onViewCompany }) {
  const [form, setForm] = useState({
    name: "",
    subdomain: "",
    contact_email: "",
    admin_email: "",
    subscription_days: 30,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const res = await createCompany(token, {
        ...form,
        subscription_days: Number(form.subscription_days) || 30,
      });
      setResult(res);
      onCreated();
    } catch (err) {
      setError(err.message || "Failed to create company");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {result ? (
          <>
            <h3 style={{ marginTop: 0 }}>Company created</h3>
            <p style={{ fontSize: 14, color: "#475569" }}>
              Provisioning has started in the background — the database, HTTPS
              subdomain, and first admin login can take up to a couple of
              minutes to finish. Watch its progress below.
            </p>
            <ProvisioningProgress
              token={token}
              companyId={result.company.id}
              initialCompany={result.company}
              onViewCompany={onViewCompany}
            />
            <div style={credentialsBoxStyle}>
              <div><strong>Login URL:</strong> https://{result.company.subdomain}.posflect.com</div>
              <div><strong>Admin email:</strong> {form.admin_email}</div>
              <div>
                <strong>Admin password:</strong> {result.admin_password}
                {result.generated_password && " (auto-generated)"}
              </div>
            </div>
            <p style={{ fontSize: 12, color: "#94a3b8" }}>
              This password is shown only once. It has also been emailed to
              the company's contact address, if SMTP is configured.
            </p>
            <button style={primaryBtnStyle} onClick={onClose}>Done</button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <h3 style={{ marginTop: 0 }}>Add company</h3>

            <label style={labelStyle}>Company name</label>
            <input style={inputStyle} value={form.name} onChange={set("name")} required />

            <label style={labelStyle}>Subdomain</label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                value={form.subdomain}
                onChange={(e) => setForm((f) => ({ ...f, subdomain: e.target.value.toLowerCase() }))}
                placeholder="acme"
                required
              />
              <span style={{ fontSize: 13, color: "#64748b" }}>.posflect.com</span>
            </div>

            <label style={labelStyle}>Contact email</label>
            <input style={inputStyle} type="email" value={form.contact_email} onChange={set("contact_email")} required />

            <label style={labelStyle}>Admin login email</label>
            <input style={inputStyle} type="email" value={form.admin_email} onChange={set("admin_email")} required />

            <label style={labelStyle}>Subscription length (days)</label>
            <input style={inputStyle} type="number" min="1" value={form.subscription_days} onChange={set("subscription_days")} required />

            {error && <div style={errorBoxStyle}>{error}</div>}

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button type="button" style={secondaryBtnStyle} onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" style={primaryBtnStyle} disabled={saving}>
                {saving ? "Creating..." : "Create company"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Companies() {
  const { token } = useContext(PlatformAuthContext);
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (status) params.status = status;
      const res = await getCompanies(token, params);
      setCompanies(res.data || []);
      setMeta(res.meta || { total: 0, page: 1, limit: 20 });
    } catch (err) {
      console.error("Failed to load companies:", err.message);
    } finally {
      setLoading(false);
    }
  }, [token, search, status]);

  useEffect(() => {
    load(1);
  }, [load]);

  const handleRetry = async (id) => {
    setRetryingId(id);
    try {
      await retryCompanyProvisioning(token, id);
      await load(meta.page);
    } catch (err) {
      alert(err.message || "Failed to retry provisioning");
    } finally {
      setRetryingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || 20)));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>Companies</h2>
        <button style={primaryBtnStyle} onClick={() => setShowAddModal(true)}>
          + Add company
        </button>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <input
          style={{ ...inputStyle, maxWidth: 260 }}
          placeholder="Search by name or subdomain..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={{ ...inputStyle, maxWidth: 180 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="provisioning">Provisioning</option>
          <option value="active">Active</option>
          <option value="grace">Grace period</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              {["Company", "Subdomain", "Status", "Expires", "", ].map((h, i) => (
                <th key={i} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={tdEmptyStyle}>Loading...</td></tr>
            ) : companies.length === 0 ? (
              <tr><td colSpan={5} style={tdEmptyStyle}>No companies found.</td></tr>
            ) : (
              companies.map((c) => (
                <tr key={c.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: "#0f172a" }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>{c.contact_email}</div>
                  </td>
                  <td style={tdStyle}>{c.subdomain}.posflect.com</td>
                  <td style={tdStyle}>
                    <StatusBadge status={c.status} />
                    <ProvisioningBadge company={c} />
                  </td>
                  <td style={tdStyle}>{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <button style={linkBtnStyle} onClick={() => navigate(`/platform/companies/${c.id}`)}>
                      View
                    </button>
                    {c.provisioning_status === "tls_failed" && (
                      <button
                        style={linkBtnStyle}
                        onClick={() => handleRetry(c.id)}
                        disabled={retryingId === c.id}
                      >
                        {retryingId === c.id ? "Retrying..." : "Retry"}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => load(p)}
              style={{
                ...secondaryBtnStyle,
                background: p === meta.page ? "#0f172a" : "#fff",
                color: p === meta.page ? "#fff" : "#334155",
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddCompanyModal
          token={token}
          onClose={() => {
            setShowAddModal(false);
            load(1);
          }}
          onCreated={() => load(1)}
          onViewCompany={(id) => {
            setShowAddModal(false);
            navigate(`/platform/companies/${id}`);
          }}
        />
      )}
    </div>
  );
}

const thStyle = { padding: "12px 16px", fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase" };
const tdStyle = { padding: "14px 16px", fontSize: 14, color: "#334155", verticalAlign: "top" };
const tdEmptyStyle = { padding: 40, textAlign: "center", color: "#94a3b8" };
const inputStyle = {
  padding: "9px 12px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  fontSize: 14,
  width: "100%",
  boxSizing: "border-box",
};
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", margin: "12px 0 4px" };
const primaryBtnStyle = {
  background: "#0f172a",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "10px 16px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};
const secondaryBtnStyle = {
  background: "#fff",
  color: "#334155",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "10px 16px",
  fontSize: 14,
  cursor: "pointer",
};
const linkBtnStyle = {
  background: "none",
  border: "none",
  color: "#2563eb",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  marginLeft: 10,
};
const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};
const modalStyle = {
  background: "#fff",
  borderRadius: 12,
  padding: 24,
  width: 420,
  maxWidth: "90vw",
  maxHeight: "90vh",
  overflowY: "auto",
};
const errorBoxStyle = {
  background: "#fee2e2",
  color: "#991b1b",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: 13,
  marginTop: 12,
};
const progressBoxStyle = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "10px 12px",
};
const credentialsBoxStyle = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: 12,
  fontSize: 13,
  lineHeight: 1.8,
  marginBottom: 12,
};
